import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { seoAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import { handleApiError, showToast, getImageUrl } from '../../utils/helpers';
import { X } from 'lucide-react';
import MediaLibraryPickerModal from '../media/MediaLibraryPickerModal';

const SeoAddKeywordModal = ({ planId, clientId, clientName, isOpen, onClose, onSaved }) => {
  const { isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [clientPosts, setClientPosts] = useState([]);
  const [form, setForm] = useState({
    keyword: '',
    publish: false,
    article_post_id: '',
    design_media_id_1: '',
    design_media_id_2: '',
    design_media_1: null,
    design_media_2: null,
  });
  const [mediaPickerField, setMediaPickerField] = useState(null);

  useEffect(() => {
    if (isOpen && clientId) {
      clientsAPI.getPosts(clientId)
        .then((res) => setClientPosts(res.data.posts || []))
        .catch(() => setClientPosts([]));
    }
  }, [isOpen, clientId]);

  useEffect(() => {
    if (isOpen) {
      setForm({
        keyword: '',
        publish: false,
        article_post_id: '',
        design_media_id_1: '',
        design_media_id_2: '',
        design_media_1: null,
        design_media_2: null,
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.keyword.trim()) {
      showToast.error(isRTL ? 'يرجى إدخال الكلمة المفتاحية' : 'Please enter a keyword');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        keyword: form.keyword.trim(),
        status: 'proposed',
        publish: form.publish,
      };
      if (form.article_post_id) payload.article_post_id = form.article_post_id;
      if (form.design_media_id_1) payload.design_media_id_1 = form.design_media_id_1;
      if (form.design_media_id_2) payload.design_media_id_2 = form.design_media_id_2;
      await seoAPI.addKeyword(planId, payload);
      showToast.success(isRTL ? 'تمت إضافة الكلمة' : 'Keyword added');
      onSaved?.();
      onClose();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const articles = clientPosts.filter((p) => p.content_type === 'blog');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isRTL ? 'إضافة كلمة مفتاحية' : 'Add keyword'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {isRTL ? 'الكلمة المفتاحية' : 'Keyword'} *
            </label>
            <input
              type="text"
              value={form.keyword}
              onChange={(e) => setForm((f) => ({ ...f, keyword: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder={isRTL ? 'اكتب الكلمة المفتاحية هنا' : 'Type keyword here'}
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="add-publish"
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setForm((f) => ({ ...f, publish: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="add-publish" className="text-sm text-slate-700">
              {isRTL ? 'جاهزة للنشر' : 'Ready to publish'}
            </label>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-4">
            <h4 className="text-sm font-medium text-slate-700">
              {isRTL ? 'ربط بالمحتوى والتصاميم (اختياري)' : 'Link to content & designs (optional)'}
            </h4>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'المحتوى (مقال)' : 'Content (article)'}
              </label>
              <select
                value={form.article_post_id}
                onChange={(e) => setForm((f) => ({ ...f, article_post_id: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{isRTL ? '-- لا يوجد --' : '-- None --'}</option>
                {articles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title || `#${p.serial_number}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'التصميم 1' : 'Design 1'}
              </label>
              <div className="flex items-center gap-2">
                {form.design_media_1 ? (
                  <>
                    <div className="w-12 h-12 rounded overflow-hidden bg-slate-100 shrink-0">
                      <img src={getImageUrl(form.design_media_1.file_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-sm text-slate-700 truncate flex-1">{form.design_media_1.file_name}</span>
                  </>
                ) : (
                  <span className="text-slate-400 text-sm">{isRTL ? 'لم يتم الاختيار' : 'Not selected'}</span>
                )}
                <Button type="button" variant="secondary" size="sm" onClick={() => setMediaPickerField('design_media_id_1')}>
                  {form.design_media_1 ? (isRTL ? 'تغيير' : 'Change') : (isRTL ? 'اختر من المكتبة' : 'Select from library')}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'التصميم 2' : 'Design 2'}
              </label>
              <div className="flex items-center gap-2">
                {form.design_media_2 ? (
                  <>
                    <div className="w-12 h-12 rounded overflow-hidden bg-slate-100 shrink-0">
                      <img src={getImageUrl(form.design_media_2.file_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-sm text-slate-700 truncate flex-1">{form.design_media_2.file_name}</span>
                  </>
                ) : (
                  <span className="text-slate-400 text-sm">{isRTL ? 'لم يتم الاختيار' : 'Not selected'}</span>
                )}
                <Button type="button" variant="secondary" size="sm" onClick={() => setMediaPickerField('design_media_id_2')}>
                  {form.design_media_2 ? (isRTL ? 'تغيير' : 'Change') : (isRTL ? 'اختر من المكتبة' : 'Select from library')}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-slate-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" loading={loading}>
              {isRTL ? 'إضافة' : 'Add'}
            </Button>
          </div>
        </form>
      </Card>
      {mediaPickerField && (
        <MediaLibraryPickerModal
          clientId={clientId}
          clientName={clientName}
          isOpen
          onClose={() => setMediaPickerField(null)}
          onSelect={(media) => {
            if (media) {
              setForm((f) => ({
                ...f,
                [mediaPickerField]: media.id,
                [mediaPickerField.replace('_id_', '_')]: media,
              }));
            } else {
              const base = mediaPickerField.replace('_id_', '_');
              setForm((f) => ({
                ...f,
                [mediaPickerField]: '',
                [base]: null,
              }));
            }
            setMediaPickerField(null);
          }}
          imageOnly
        />
      )}
    </div>
  );
};

export default SeoAddKeywordModal;
